'use client';
import type {IBrowserSDK} from '@splitsoftware/splitio/types/splitio';
import {
  SplitFactory,
  SplitFactoryProvider,
  useSplitTreatments,
} from '@splitsoftware/splitio-react';
import {type PropsWithChildren, useEffect, useState} from 'react';
import {useCookies} from 'react-cookie';
import {v4 as uuidv4} from 'uuid';
import {isRequestedFromGoogleProxy} from '../utils/domain';
import {getMetroCodeByMarketCode} from './MarketMetroMap';

interface UserAttributesType {
  address?: string;
  market?: string;
}

export interface FlagConfig<ValueType> {
  key: string;
  description: string;
  defaultValue: ValueType;
  options?: ValueType[];
}

const getUserIdentifier = () => {
  const cookieName = 'splitUniqueIdentifier';
  const [cookies, setCookie] = useCookies([cookieName]);
  let userIdentifier = cookies.splitUniqueIdentifier;
  if (!userIdentifier) {
    userIdentifier = uuidv4();
    setCookie(cookieName, userIdentifier, {
      domain: 'gfiber.com',
      secure: true,
      sameSite: 'strict',
      httpOnly: false,
    });
  }
  return userIdentifier;
};

export const SplitFlagProvider = ({children}: PropsWithChildren) => {
  const userIdentifier = getUserIdentifier();
  const [splitFactory, setSplitFactory] = useState<IBrowserSDK | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup needed on unmount
  useEffect(() => {
    const splitKey = process.env.NEXT_PUBLIC_SPLIT_CLIENT_ID;
    // Guard: Only initialize if we have a key and aren't on a Google Proxy
    if (!isRequestedFromGoogleProxy() && splitKey) {
      const factory = SplitFactory({
        core: {
          authorizationKey: splitKey,
          key: userIdentifier,
        },
      });
      setSplitFactory(factory);
      //cleaup split instance on unmount
      return () => {
        if (splitFactory) {
          splitFactory.client().destroy(); // Cleanup when unmount
        }
      };
    }
  }, []);

  if (isRequestedFromGoogleProxy() || !splitFactory) {
    return <>{children}</>;
  }

  return (
    <SplitFactoryProvider factory={splitFactory}>
      {children}
    </SplitFactoryProvider>
  );
};

const getTreatments = (
  names: string[],
  userAttributes?: UserAttributesType,
  fallbackValue = null,
) => {
  const treatments: {[key: string]: string | null} = {};
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: Keep the existing Google Proxy guard behavior unchanged.
    const {treatments: initialTreatments, isReady} = useSplitTreatments({
      names,
      attributes: {
        address: userAttributes?.address || '',
        market: userAttributes?.market || '',
        metro: getMetroCodeByMarketCode(userAttributes?.market) || '',
      },
    });
    for (const feature in initialTreatments) {
      const {treatment: defaultTreatmentValue} =
        initialTreatments[`${feature}`];

      // set fallbackValue if treatment is control
      if (defaultTreatmentValue === 'control') {
        treatments[`${feature}`] = fallbackValue;
      } else {
        treatments[`${feature}`] = defaultTreatmentValue;
      }
    }
    return {treatments, isReady};
  } catch {
    // in case of failure always return isReady false
    return {treatments, isReady: false};
  }
};

export const useTreatment = (
  name: string,
  userAttributes?: UserAttributesType,
  fallbackValue = null,
) => {
  if (isRequestedFromGoogleProxy()) {
    return {treatment: null, isReady: true};
  }
  const {treatments, isReady} = getTreatments(
    [name],
    userAttributes,
    fallbackValue,
  );

  return {treatment: treatments[`${name}`], isReady};
};

export const useTreatments = (
  names: string[],
  userAttributes?: UserAttributesType,
  fallbackValue = null,
) => {
  if (isRequestedFromGoogleProxy()) {
    return {treatments: null, isReady: true};
  }
  const {treatments, isReady} = getTreatments(
    names,
    userAttributes,
    fallbackValue,
  );

  return {treatments, isReady};
};

/**
 * useFlag evaluates a single feature flag.
 * Maps 'on'/'off' treatments to true/false for boolean flags.
 * Returns defaultValue if treatment is 'control' or SDK is not ready.
 */
export function useFlag<T = boolean>(
  flag: FlagConfig<T>,
  userAttributes?: UserAttributesType,
  fallbackValue = null,
): T {
  const {treatment} = useTreatment(flag.key, userAttributes, fallbackValue);

  if (!treatment || treatment === 'control') {
    return flag.defaultValue;
  }

  if (typeof flag.defaultValue === 'boolean') {
    if (treatment === 'on') return true as unknown as T;
    if (treatment === 'off') return false as unknown as T;
  }

  return treatment as unknown as T;
}
